import { Center, Stack, Text } from '@chakra-ui/react'
import type { DateTime } from 'luxon'
import { useRouter } from 'next/router'
import type { ReactElement } from 'react'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import { BOOTCAMP_AVERAGE_LEARNER_SPEAKING_PERCENTAGE } from '@/client/services/satBootcamp'
import { getSessionUrl } from '@/client/services/session'
import * as url from '@/client/services/url'

import SessionTalkTimeGauge from '@/components/Session/SessionMetrics/SessionTalkTimeGauge'
import ShwLink from '@/components/ShwLink'
import Alert from '@/components/UI/Alert'
import ModalV3 from '@/components/UI/Modal/ModalV3'

import JoinSessionButton from './JoinSessionButton'
import type { ListItemWithEmphasis } from './types'
import UnorderedListWithEmphasis from './UnorderedListWithEmphasis'

const LEARNER_JOIN_LIST_ITEMS: ListItemWithEmphasis[] = [
  {
    emphasizedText: 'Share',
    normalText: 'your thoughts, questions, and opinions—your tutor is here to help!',
  },
  {
    emphasizedText: 'Respect',
    normalText:
      'your tutor and fellow learners by using class-appropriate dress, behavior, and language.',
  },
  {
    emphasizedText: 'Protect',
    normalText:
      'your privacy: please do not exchange contact information or your personal Zoom link.',
  },
]

export default function LearnerJoinSessionModal({
  open,
  onClose,
  sessionId,
  seriesId,
  topicName,
  recordingPolicyAcknowledgedAt,
  onAcknowledgeRecordingPolicy,
  customRedirectUrl,
  showSATBootcampLearnerFeedback,
  pastSessionTutorSpeakingPercentage,
}: {
  open: boolean
  onClose: () => void
  sessionId: number
  seriesId: number | null
  topicName: string
  recordingPolicyAcknowledgedAt: DateTime | null
  onAcknowledgeRecordingPolicy: () => Promise<void>
  customRedirectUrl?: string
  showSATBootcampLearnerFeedback: boolean
  pastSessionTutorSpeakingPercentage: number | null
}): ReactElement {
  const router = useRouter()

  const sessionOrSeriesUrl = getSessionUrl({ sessionId, seriesId, topicName })
  const worldsSessionOrSeriesUrl = url.getSessionOrSeriesUrl({ sessionId, seriesId })

  const shouldRedirect =
    !url.helpers.routerMatchesUrl(router, sessionOrSeriesUrl) &&
    !url.helpers.routerMatchesUrl(router, worldsSessionOrSeriesUrl)

  const pastLearnerSpeakingPercentage =
    pastSessionTutorSpeakingPercentage == null ? null : 100 - pastSessionTutorSpeakingPercentage

  const [handleJoinSession, isJoiningSession] = useAPIWrapper(
    async () => {
      onClose()
      if (recordingPolicyAcknowledgedAt == null) {
        await onAcknowledgeRecordingPolicy()
      }
      if (shouldRedirect) {
        await router.push(customRedirectUrl ?? sessionOrSeriesUrl)
      }
    },
    [
      onClose,
      router,
      sessionOrSeriesUrl,
      shouldRedirect,
      recordingPolicyAcknowledgedAt,
      onAcknowledgeRecordingPolicy,
      customRedirectUrl,
    ],
    'Learner Join Session'
  )

  return (
    <ModalV3
      size="xl"
      open={open}
      onClose={onClose}
      title="Join Session"
      body={() => (
        <>
          <Stack direction="column" spacing="8px">
            {showSATBootcampLearnerFeedback && pastSessionTutorSpeakingPercentage != null ? (
              <>
                <Text>
                  Last session, the learners in your SAT Bootcamp group talked (or messaged the
                  chat) for about {pastLearnerSpeakingPercentage}% of the time, vs.{' '}
                  {pastSessionTutorSpeakingPercentage}% from your tutor.
                </Text>
                <Center mt="12px">
                  <SessionTalkTimeGauge
                    learnerSpeakingPercentage={100 - pastSessionTutorSpeakingPercentage}
                    bootcampAveragePercentage={BOOTCAMP_AVERAGE_LEARNER_SPEAKING_PERCENTAGE}
                  />
                </Center>
                <Text>
                  Remember: if you&apos;re struggling with something,{' '}
                  <b>some of your peers are probably struggling with the same thing!</b> Sharing
                  your questions and ideas can maximize everyone&apos;s learning during the session.
                </Text>
              </>
            ) : (
              <>
                <Text textAlign="left">Before joining this session, please remember to:</Text>
                <UnorderedListWithEmphasis listItems={LEARNER_JOIN_LIST_ITEMS} />
                <Text>
                  Be sure to report any inappropriate behavior on the{' '}
                  <ShwLink href={url.getReportUrl()}>feedback form</ShwLink>. And of course, have
                  fun!
                </Text>
              </>
            )}
          </Stack>
          {recordingPolicyAcknowledgedAt == null ? (
            <Alert variant="info-blue" mt="16px">
              Note that all sessions are recorded for safety and quality purposes. To learn more
              about the new details of this policy, click{' '}
              <ShwLink href={url.faq.getArticleUrl('zoom-recording-policy')}>here</ShwLink>.
            </Alert>
          ) : null}
        </>
      )}
      rightFooterContent={() => (
        <JoinSessionButton
          isHost={false}
          sessionId={sessionId}
          topicName={topicName}
          isDisabled={isJoiningSession}
          onClick={handleJoinSession}
        />
      )}
    />
  )
}
